import React from 'react';
import { MessageSquare, Lightbulb, Target, BarChart3 } from 'lucide-react';
import { CompetitorData } from '../../types/dataTypes';

interface CompetitorInsightsProps {
  data: CompetitorData;
}

const CompetitorInsights: React.FC<CompetitorInsightsProps> = ({ data }) => {
  return (
    <section className="mt-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          AI-Generated Insights
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-all duration-300 transform hover:shadow-lg lg:col-span-2">
          <div className="flex items-center mb-4">
            <MessageSquare className="text-blue-600 dark:text-blue-400 mr-2" size={20} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Ad Messaging Analysis
            </h3>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              {data.insights.messagingAnalysis.summary}
            </p>
            
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Common Themes</h4>
              <div className="flex flex-wrap gap-2">
                {data.insights.messagingAnalysis.themes.map((theme, idx) => (
                  <span 
                    key={idx}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                  >
                    {theme}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-all duration-300 transform hover:shadow-lg">
          <div className="flex items-center mb-4">
            <Target className="text-blue-600 dark:text-blue-400 mr-2" size={20} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              CTA Analysis
            </h3>
          </div>
          
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            {data.insights.ctaAnalysis.summary}
          </p>
          
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Popular CTAs</h4>
            {data.insights.ctaAnalysis.popularCtas.map((cta, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between p-2 border border-gray-100 dark:border-gray-700 rounded-md"
              >
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{cta.text}</span>
                <div className="flex items-center">
                  <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5 mr-2">
                    <div 
                      className="bg-blue-600 h-1.5 rounded-full" 
                      style={{ width: `${cta.frequency * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{Math.round(cta.frequency * 100)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-all duration-300 transform hover:shadow-lg lg:col-span-3">
          <div className="flex items-center mb-4">
            <Lightbulb className="text-blue-600 dark:text-blue-400 mr-2" size={20} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Competitive Positioning Analysis
            </h3>
          </div>
          
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            {data.insights.positioningAnalysis.summary}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Key Differentiators</h4>
              <ul className="space-y-2">
                {data.insights.positioningAnalysis.differentiators.map((diff, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 mr-2 mt-0.5 flex-shrink-0">
                      <span className="text-xs font-bold">✓</span>
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{diff}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Areas for Improvement</h4>
              <ul className="space-y-2">
                {data.insights.positioningAnalysis.improvements.map((improvement, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 mr-2 mt-0.5 flex-shrink-0">
                      <span className="text-xs font-bold">!</span>
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{improvement}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-3">
              <BarChart3 className="text-blue-600 dark:text-blue-400 mr-2" size={18} />
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Market Position Comparison</h4>
            </div>
            <div className="relative pt-8 pb-4 px-4">
              <div className="absolute top-0 left-0 right-0 flex justify-between text-xs text-gray-500 dark:text-gray-400 px-4">
                <span>Value-Focused</span>
                <span>Premium</span>
              </div>
              <div className="absolute left-0 top-8 bottom-0 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 py-2">
                <span>Broad<br/>Audience</span>
                <span>Niche<br/>Focused</span>
              </div>
              <div className="relative h-48 ml-12 border-l border-b border-gray-300 dark:border-gray-600">
                {data.insights.positioningAnalysis.marketPositions.map((position, idx) => (
                  <div 
                    key={idx}
                    className="absolute w-8 h-8 rounded-full shadow-md flex items-center justify-center transform -translate-x-4 -translate-y-4 transition-all duration-300 hover:scale-110"
                    style={{ 
                      left: `${position.xValue * 100}%`, 
                      top: `${position.yValue * 100}%`,
                      backgroundColor: position.isYou ? '#3B82F6' : '#9CA3AF',
                      borderWidth: position.isYou ? 2 : 0,
                      borderColor: '#DBEAFE',
                    }}
                  >
                    <span className="text-white text-xs font-bold" title={position.name}>
                      {position.name.charAt(0)}
                    </span>
                    <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs font-medium text-gray-700 dark:text-gray-300">
                      {position.name.length > 8 ? `${position.name.substring(0, 8)}...` : position.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompetitorInsights;